import React, { useState } from 'react';
import {
  Button, Col, Row, Spin,
  Table, Form,
  Select,
  notification,
  Input,
  DatePicker,
  Tooltip,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import './DownloadNf.scss';
import { FilePdfOutlined, FileTextFilled } from '@ant-design/icons';
import FilterIcon from 'mdi-react/FilterIcon';
import moment from 'moment';
import {
  get, isSuperAgentError, patch, RequestWrapper, ResponseWrapper,
} from '../../dataSource/requests';
import { downloadArchive, downloadPdf, downloadXml } from '../../utils/helpers';
import { MONTH } from '../../utils/constants';
import { getUserBP } from '../../utils/users';

interface TablePagination {
  current: number | undefined,
  defaultCurrent: number | undefined,
  pageSize: number | undefined,
  showQuickJumper: boolean | {
    goButton?: React.ReactNode;
  },
  total: number | undefined,
  itemsInPage?: number | undefined,
}

type TFilterInvoice = {
  companyName: string
  cooperativeName: string
  invoiceNumber: string
  month: string
  nfeNumber: string
  nfeValue: string
  year: string
  direct: string
  nfeType: string
}

const DownloadNf = (): JSX.Element => {
  const navigate = useNavigate();

  const [formInstance] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [tableDataSource, setTableDataSource] = useState<Array<any>>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tablePagination, setTablePagination] = useState<TablePagination>({
    current: 1,
    defaultCurrent: 1,
    pageSize: 15,
    showQuickJumper: true,
    total: 0,
  });

  const dowloadPdf = async (pdfIdentity:number, type:string, typeFile:string):Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let requestReference: RequestWrapper;

    setLoading(true);

    if (type === 'Produto') {
      requestReference = get({
        url: `/invoice/downloadSap${typeFile}/${pdfIdentity}`,
        frontConfigs: {
          navigateFunction: navigate,
        },
      });
    } else {
      requestReference = get({
        url: `/invoice/downloadNfeFile/${pdfIdentity}`,
        frontConfigs: {
          navigateFunction: navigate,
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
        responseType: 'blob',
      });
    }

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 23003):
        downloadPdf(response?.body?.nfeFile, `nota-fiscal-${moment().format('yyyy-MM-DD')}`);
        break;

      case (response != null && response?.body?.code === 23004):
        downloadXml(response?.body?.nfeFile, `nota-fiscal-${moment().format('yyyy-MM-DD')}`);
        break;

      case (response != null && response?.status === 200):
        downloadArchive(response?.body, response?.body?.type, `nota-${moment().format('yyyy-MM-DD')}`);
        break;

      case (responseError != null && responseError?.body?.code === -25006): {
        notification.error({
          message: 'Erro',
          description: 'O arquivo da nota fiscal em PDF não foi encontrado.',
        });
        break;
      }

      case (responseError != null && responseError?.body?.code === -25007): {
        notification.error({
          message: 'Erro',
          description: 'O arquivo da nota fiscal em XML não foi encontrado.',
        });
        break;
      }

      case (responseError != null && responseError?.body?.apiResponse?.code === -25005): {
        notification.error({
          message: 'Conexão com SAP',
          description: 'SAP excedeu o tempo limite de resposta para solicitação',
        });
        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao tentar baixar o arquivo, por favor tente novamente mais tarde.',
        });
        break;
    }

    setLoading(false);
  };

  const formaterFilter = ():TFilterInvoice => {
    const values = formInstance.getFieldsValue();

    return {
      ...values,
      month: values.month ? MONTH[moment(values.month).month()] : '',
      year: values.year ? moment(values.year).year().toString() : '',
    };
  };

  const executeFilter = async (args: {
    page?: number,
    pageSize?: number,
    orderBy?: string,
    orderByDir?: string,
  } = {
    page: 0,
    pageSize: 15,
    orderBy: 'emissionDate',
    orderByDir: 'DESC',
  }): Promise<void> => {
    setTableLoading(true);
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    const values = formaterFilter();
    const bp = getUserBP();

    const argsQuery = {
      orderBy: ('orderBy' in args) ? args.orderBy : 'emissionDate',
      page: ('page' in args && args.page >= 1) ? args?.page : 0,
      pageSize: ('pageSize' in args) ? args.pageSize : 15,
      sort: ('orderByDir' in args) ? args.orderByDir : 'DESC',
    };

    const requestReference: RequestWrapper = patch({
      url: '/invoice/cooperado/listNFe',
      params: {
        ...Object.fromEntries(
          Object
            .entries(values)
            .filter(
              ([, elementValue]) => {
                return elementValue !== null;
              },
            ),
        ),
      },
      query: {
        ...Object.fromEntries(
          Object
            .entries(argsQuery)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 23002): {
        const pagination = tablePagination;
        pagination.pageSize = response?.body?.paginationInfo?.maxSize;
        pagination.current = response?.body?.paginationInfo?.pageNumber + 1;
        pagination.total = response?.body?.paginationInfo?.totalElements;

        if (response?.body?.nfeSapOutput.length > 0) {
          setTableDataSource(response?.body?.nfeSapOutput);
        } else {
          setTableDataSource([]);
          notification.info({
            message: 'Atenção',
            description: `Nenhuma nota fiscal foi encontrada para o BP ${bp} para a data pesquisada.`,
          });
          break;
        }

        setTablePagination((previous) => {
          return ({
            ...previous,
            current: pagination.current,
            defaultCurrent: pagination.defaultCurrent,
            pageSize: pagination.pageSize,
            showQuickJumper: pagination.showQuickJumper,
            total: pagination.total,
          });
        });
      } break;

      case (response != null && response?.body?.code === -25004): {
        setTableDataSource([]);
        notification.info({
          message: 'Atenção',
          description: `Nenhuma nota fiscal foi encontrada para o BP ${bp} para a data pesquisada.`,
        });
        break;
      }

      case (responseError?.body?.apiResponse?.code === -25005): {
        notification.error({
          message: 'Conexão com SAP',
          description: 'SAP excedeu o tempo limite de resposta para solicitação',
        });
        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao pesquisar as notas fiscais, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableLoading(false);
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any): void => {
    setTableLoading(true);

    setTablePagination((previous) => {
      return ({
        ...previous,
        current: pagination.current || previous.current,
        defaultCurrent: pagination.defaultCurrent || previous.defaultCurrent,
        pageSize: pagination.pageSize || previous.pageSize,
        showQuickJumper: pagination.showQuickJumper
          || previous.showQuickJumper,
        total: pagination.total || previous.total,
      });
    });

    let finalOrderDir;

    if (sorter?.field && sorter?.order) {
      switch (true) {
        case (sorter.order === 'descend'):
          finalOrderDir = 'DESC';
          break;

        case (sorter.order === 'ascend'):
        case (sorter.order == null):
        default:
          finalOrderDir = 'ASC';
          break;
      }
    }

    executeFilter({
      page: (pagination?.current)
        ? pagination?.current - 1
        : 0,
      orderBy: sorter?.field || 'emissionDate',
      orderByDir: finalOrderDir || 'DESC',
      pageSize: pagination.pageSize,
    });
  };

  return (
    <div className="DownloadNf">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Row gutter={[24, 24]}>
          <Col xs={{ span: 24 }}>
            <Row
              gutter={[24, 8]}
              className="title-container"
            >
              <Col>
                <span className="page-title">
                  Nota Fiscal de produto e serviço
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
        <Form
          scrollToFirstError
          noValidate
          layout="vertical"
          form={formInstance}
          onFinish={
          async (): Promise<void> => {
            await executeFilter();
          }
        }
        >
          <Row
            gutter={24}
            className="filters-container"
          >
            <Col xs={{ span: 24 }} lg={{ span: 24 }}>
              <Row gutter={[8, 24]} className="inputsForm">
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Ano do faturamento"
                    name="year"
                    rules={[{
                      required: true,
                      message: 'Campo obrigatório',
                    }]}
                  >
                    <DatePicker picker="year" style={{ width: '100%' }} format="YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Mês do faturamento"
                    name="month"
                    rules={[{
                      required: true,
                      message: 'Campo obrigatório',
                    }]}
                  >
                    <DatePicker picker="month" style={{ width: '100%' }} format="MMMM" />
                  </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Nome da propriedade"
                    name="cooperativeName"
                    rules={[]}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Digite o nome da propriedade"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[8, 24]} className="inputsForm">
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Nome da empresa emissora"
                    name="companyName"
                    rules={[]}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Digite o nome da empresa emissora"
                    />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Número da nota fiscal"
                    name="nfeNumber"
                    rules={[]}
                  >
                    <Input
                      type="number"
                      min={0}
                      style={{ width: '100%' }}
                      placeholder="Digite o número da nota fiscal"
                    />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Número do faturamento da nota fiscal"
                    name="invoiceNumber"
                    rules={[]}
                  >
                    <Input
                      type="number"
                      min={0}
                      style={{ width: '100%' }}
                      placeholder="Digite o número do faturamento da nota fiscal"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[8, 24]} className="inputsForm">
                <Col xs={{ span: 24 }} lg={{ span: 6 }}>
                  <Form.Item label="Direção da movimentação" name="direct" rules={[]}>
                    <Select
                      showSearch
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Selecione o tipo de movimentação"
                    >
                      <Select.Option value="entrada">
                        Entrada
                      </Select.Option>
                      <Select.Option value="Saída">
                        Saída
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} lg={{ span: 6 }}>
                  <Form.Item label="Tipo da nota fiscal" name="nfeType" rules={[]}>
                    <Select
                      showSearch
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Selecione o tipo da nota fiscal"
                    >
                      <Select.Option value="Produto">
                        Produto
                      </Select.Option>
                      <Select.Option value="Serviço">
                        Serviço
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 6 }}>
                  <Form.Item
                    label="Valor da nota fiscal"
                    name="nfeValue"
                    rules={[]}
                  >
                    <Input
                      type="number"
                      min={0}
                      style={{ width: '100%' }}
                      placeholder="Digite o valor da nota fiscal"
                    />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 6 }}>
                  <Form.Item
                    label=" "
                  >
                    <Button
                      className="btn-filter"
                      type="primary"
                      htmlType="submit"
                    >
                      <FilterIcon />
                      Filtrar
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Col>

          </Row>
        </Form>
        {tableDataSource.length > 0 && (
          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    className="AccountDeletionRequests__Table"
                    columns={[
                      {
                        title: <div className="table-titles">Nome da propriedade</div>,
                        key: 'cooperativeName',
                        dataIndex: 'cooperativeName',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 250,
                      },
                      {
                        title: <div className="table-titles">Nome da empresa emissora</div>,
                        key: 'companyName',
                        dataIndex: 'companyName',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 250,
                      },
                      {
                        title: <div className="table-titles">Data do faturamento da nota fiscal</div>,
                        key: 'emissionDate',
                        dataIndex: 'emissionDate',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 300,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.emissionDate) {
                            return moment(record.emissionDate).utc().format('DD/MM/YYYY');
                          }
                          return '-';
                        },
                      },
                      {
                        title: <div className="table-titles">Número da nota fiscal</div>,
                        key: 'nfeNumber',
                        dataIndex: 'nfeNumber',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 210,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.nfeNumber) {
                            return record.nfeNumber;
                          }
                          return ' - ';
                        },
                      },
                      {
                        title: <div className="table-titles">Número do faturamento da nota fiscal</div>,
                        key: 'invoiceId',
                        dataIndex: 'invoiceId',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 350,
                      },
                      {
                        title: <div className="table-titles">Direção da movimentação</div>,
                        key: 'direct',
                        dataIndex: 'direct',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 250,
                      },
                      {
                        title: <div className="table-titles">Tipo da nota fiscal</div>,
                        key: 'nfeType',
                        dataIndex: 'nfeType',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 200,
                      },
                      {
                        title: <div className="table-titles">Valor total da nota fiscal</div>,
                        key: 'nfeValue',
                        dataIndex: 'nfeValue',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 250,
                      },
                      {
                        title: <div className="table-titles">Download</div>,
                        dataIndex: 'id',
                        key: 'acoes',
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => (
                          <div className="btn-download">
                            <Tooltip title="Arquivo em PDF">
                              <span>
                                { record.pdfId ? (
                                  <Button
                                    icon={<FilePdfOutlined />}
                                    onClick={
                                async (): Promise<void> => {
                                  await dowloadPdf(record.pdfId, record.nfeType, 'Pdf');
                                }
                              }
                                  />
                                ) : (' - ')}
                              </span>
                            </Tooltip>
                            <Tooltip title="Arquivo em XML">
                              <span>
                                { record.xmlId ? (
                                  <Button
                                    icon={<FileTextFilled />}
                                    onClick={
                              async (): Promise<void> => {
                                await dowloadPdf(record.xmlId, record.nfeType, 'Xml');
                              }
                            }
                                  />
                                ) : (' - ')}
                              </span>
                            </Tooltip>
                          </div>
                        ),
                        width: 110,
                      },
                    ]}
                    dataSource={tableDataSource}
                    loading={tableLoading}
                    rowKey={(record): string => record.id as unknown as string}
                    pagination={{
                      defaultCurrent: tablePagination.defaultCurrent || 1,
                      current: tablePagination.current || 1,
                      pageSize: tablePagination.pageSize || 15,
                      total: tablePagination.total || 0,
                      showQuickJumper: tablePagination.showQuickJumper || true,
                      pageSizeOptions: ['1', '15', '50', '100'],
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    scroll={{
                      x: '100vw',
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Spin>
    </div>
  );
};

export default DownloadNf;
